/**
 * User journey and funnel tracking
 */

const JOURNEY_KEY = 'wt_journey';
const MAX_JOURNEY_EVENTS = 50;

export interface JourneyEvent {
  type: 'pageview' | 'click' | 'custom';
  name: string;
  url: string;
  timestamp: string;
  properties?: Record<string, any>;
}

export interface Journey {
  events: JourneyEvent[];
  startTime: string;
  lastUpdate: string;
}

/**
 * User Journey Tracker - tracks the complete path a user takes through your app
 */
export class JourneyTracker {
  private journey: Journey;

  constructor() {
    this.journey = this.loadJourney();
  }

  /**
   * Load journey from storage
   */
  private loadJourney(): Journey {
    try {
      const stored = localStorage.getItem(JOURNEY_KEY);
      if (stored) {
        const journey = JSON.parse(stored);
        // Check if journey is from the same session (within last 30 minutes)
        const lastUpdate = new Date(journey.lastUpdate).getTime();
        if (Date.now() - lastUpdate < 30 * 60 * 1000) {
          return journey;
        }
      }
    } catch {
      // Failed to load, start fresh
    }

    return {
      events: [],
      startTime: new Date().toISOString(),
      lastUpdate: new Date().toISOString(),
    };
  }

  /**
   * Save journey to storage
   */
  private saveJourney(): void {
    try {
      this.journey.lastUpdate = new Date().toISOString();
      localStorage.setItem(JOURNEY_KEY, JSON.stringify(this.journey));
    } catch {
      // Silent fail
    }
  }

  /**
   * Add event to journey
   */
  addEvent(event: JourneyEvent): void {
    this.journey.events.push(event);

    // Keep only the last N events to prevent storage issues
    if (this.journey.events.length > MAX_JOURNEY_EVENTS) {
      this.journey.events = this.journey.events.slice(-MAX_JOURNEY_EVENTS);
    }

    this.saveJourney();
  }

  /**
   * Get current journey
   */
  getJourney(): Journey {
    return { ...this.journey };
  }

  /**
   * Get journey summary
   */
  getSummary() {
    const events = this.journey.events;
    const pageviews = events.filter(e => e.type === 'pageview');
    const clicks = events.filter(e => e.type === 'click');
    const custom = events.filter(e => e.type === 'custom');

    // Extract unique pages visited
    const pagesVisited = [...new Set(pageviews.map(e => e.url))];

    // Calculate journey duration
    const startTime = new Date(this.journey.startTime).getTime();
    const lastUpdate = new Date(this.journey.lastUpdate).getTime();
    const durationMinutes = Math.round((lastUpdate - startTime) / 1000 / 60);

    return {
      totalEvents: events.length,
      pageviews: pageviews.length,
      clicks: clicks.length,
      customEvents: custom.length,
      pagesVisited: pagesVisited.length,
      uniquePages: pagesVisited,
      durationMinutes,
      startTime: this.journey.startTime,
      lastUpdate: this.journey.lastUpdate,
    };
  }

  /**
   * Get recent events
   */
  getRecentEvents(limit: number = 10): JourneyEvent[] {
    return this.journey.events.slice(-limit);
  }

  /**
   * Clear journey
   */
  clear(): void {
    this.journey = {
      events: [],
      startTime: new Date().toISOString(),
      lastUpdate: new Date().toISOString(),
    };
    try {
      localStorage.removeItem(JOURNEY_KEY);
    } catch {
      // Silent fail
    }
  }
}

/**
 * Funnel Tracker - track user progress through defined funnels
 */
export class FunnelTracker {
  private funnels: Map<string, FunnelDefinition> = new Map();
  private completedSteps: Map<string, Set<string>> = new Map();

  defineFunnel(funnel: FunnelDefinition): void {
    this.funnels.set(funnel.name, funnel);
    this.completedSteps.set(funnel.name, new Set());
  }

  /**
   * Track funnel step completion
   */
  trackStep(funnelName: string, stepName: string, properties?: Record<string, any>): FunnelStepResult | null {
    const funnel = this.funnels.get(funnelName);
    if (!funnel) return null;

    const completed = this.completedSteps.get(funnelName)!;
    const step = funnel.steps.find(s => s.name === stepName);

    if (!step) return null;

    // Mark step as completed
    completed.add(stepName);

    // Calculate progress
    const stepIndex = funnel.steps.indexOf(step);
    const progress = ((stepIndex + 1) / funnel.steps.length) * 100;
    const isComplete = stepIndex === funnel.steps.length - 1;

    return {
      funnelName,
      stepName,
      stepIndex,
      progress,
      isComplete,
      nextStep: !isComplete ? funnel.steps[stepIndex + 1]?.name : null,
      properties,
    };
  }

  /**
   * Get funnel progress
   */
  getFunnelProgress(funnelName: string): FunnelProgress | null {
    const funnel = this.funnels.get(funnelName);
    if (!funnel) return null;

    const completed = this.completedSteps.get(funnelName)!;
    const completedSteps = funnel.steps.filter(s => completed.has(s.name));
    const progress = (completedSteps.length / funnel.steps.length) * 100;
    const currentStepIndex = completedSteps.length;
    const isComplete = currentStepIndex === funnel.steps.length;

    return {
      funnelName,
      totalSteps: funnel.steps.length,
      completedSteps: completedSteps.length,
      progress,
      currentStep: !isComplete ? funnel.steps[currentStepIndex]?.name : null,
      isComplete,
    };
  }

  /**
   * Reset funnel progress
   */
  resetFunnel(funnelName: string): void {
    this.completedSteps.set(funnelName, new Set());
  }

  /**
   * Get all funnels status
   */
  getAllFunnels(): FunnelProgress[] {
    return Array.from(this.funnels.keys())
      .map(name => this.getFunnelProgress(name))
      .filter((f): f is FunnelProgress => f !== null);
  }
}

export interface FunnelDefinition {
  name: string;
  steps: FunnelStep[];
}

export interface FunnelStep {
  name: string;
  description?: string;
}

export interface FunnelStepResult {
  funnelName: string;
  stepName: string;
  stepIndex: number;
  progress: number;
  isComplete: boolean;
  nextStep: string | null;
  properties?: Record<string, any>;
}

export interface FunnelProgress {
  funnelName: string;
  totalSteps: number;
  completedSteps: number;
  progress: number;
  currentStep: string | null;
  isComplete: boolean;
}

/**
 * Attribution Tracker - track where users come from
 */
export class AttributionTracker {
  /**
   * Parse UTM parameters from URL
   */
  static getUTMParameters(url: string = window.location.href): Record<string, string> {
    const params = new URL(url).searchParams;
    const utmParams: Record<string, string> = {};

    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = params.get(param);
      if (value) {
        utmParams[param] = value;
      }
    });

    return utmParams;
  }

  /**
   * Get referrer source type
   */
  static getReferrerType(referrer: string): string {
    if (!referrer) return 'direct';

    try {
      const referrerDomain = new URL(referrer).hostname;
      const currentDomain = window.location.hostname;

      if (referrerDomain === currentDomain) {
        return 'internal';
      }

      // Check for common search engines
      if (/google|bing|yahoo|duckduckgo|baidu/.test(referrerDomain)) {
        return 'search';
      }

      // Check for social media
      if (/facebook|twitter|linkedin|instagram|pinterest|reddit|tiktok/.test(referrerDomain)) {
        return 'social';
      }

      // Check for email
      if (/mail\.|outlook|gmail/.test(referrerDomain)) {
        return 'email';
      }

      return 'referral';
    } catch {
      return 'unknown';
    }
  }

  /**
   * Get comprehensive attribution data
   */
  static getAttribution() {
    const utmParams = this.getUTMParameters();
    const referrer = document.referrer;
    const referrerType = this.getReferrerType(referrer);

    return {
      utmParams,
      referrer,
      referrerType,
      landingPage: window.location.href,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Store first-touch attribution (persists across sessions)
   */
  static storeFirstTouch(): void {
    const key = 'wt_first_touch';
    try {
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, JSON.stringify(this.getAttribution()));
      }
    } catch {
      // Silent fail
    }
  }

  /**
   * Get first-touch attribution
   */
  static getFirstTouch(): any {
    try {
      const stored = localStorage.getItem('wt_first_touch');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }
}
